/**
 * `/dashboard/plans` — every Resource Plan this actor can see (RP-15.1's list, promoted to
 * a page of its own so plans are reachable without going via a Solution).
 *
 * A ribbon of review-status tiles (Open → In Review → Staffed) sits above the list: each
 * shows how many of the plans in view are in that group, and clicking one filters to it.
 *
 * Scope is enforced server-side: an [ACCT] user sees only the plans on Accounts they own,
 * and an actor who owns none sees nothing — never the whole book.
 *
 * Creating a plan needs a Solution, so the "New Plan" flow starts by picking one. The
 * Account must already exist (RP-D2, settled): plans attach to Accounts, they do not
 * create them.
 */

import { useRouter } from "@tanstack/react-router";
import { Columns3, FileSpreadsheet, SlidersHorizontal, X } from "lucide-react";
import { useMemo, useState } from "react";
import { FilterPopover } from "@/components/dashboard/solutions-filters/FilterPopover";
import {
  ChipRow,
  ChipToggle,
  FilterSection,
  SearchableMultiSelect,
} from "@/components/dashboard/solutions-filters/filter-controls";
import { PlanReviewTiles } from "@/components/plans/PlanReviewTiles";
import { PlanSetupForm } from "@/components/plans/PlanSetupForm";
import { PlansTable } from "@/components/plans/PlansTable";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useSolutionPickerOptions } from "@/hooks/use-dashboard-solutions";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { usePlanListColumns } from "@/hooks/use-plan-list-columns";
import { usePlanReviewFilter } from "@/hooks/use-plan-review-filter";
import { useResourcePlans } from "@/hooks/use-resource-plans";
import { PLAN_LIST_COLUMNS } from "@/lib/plan-list-columns";
import { matchesPlanSearch } from "@/lib/plan-list-search";
import {
  countPlanReviewStages,
  matchesReviewStageFilter,
  PLAN_REVIEW_STAGE_COPY,
} from "@/lib/plan-review-stage";
import { planStatusLabel, resourcingStatusLabel } from "@/lib/plan-status-labels";
import { isResourcePlanStatus, SELECTABLE_PLAN_STATUSES } from "@/lib/resource-plan-lifecycle";
import type { ResourcePlanSummaryView } from "@/schemas/resource-plans";
import { PLAN_RESOURCING_STATUSES } from "@/schemas/resource-plans";

/**
 * §4.6 statuses that mean the plan is no longer live. Everything else is "current" — the
 * filter key stays `open`, but it reads "Current" so it never looks like the Open status
 * (what Draft is called since 2026-10-28).
 */
const INACTIVE_STATUSES: readonly string[] = ["Lost", "Closed", "Superseded"];

function matchesStatusFilter(plan: ResourcePlanSummaryView, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "open") return !INACTIVE_STATUSES.includes(plan.status);
  if (filter === "inactive") return INACTIVE_STATUSES.includes(plan.status);
  // A specific status was already applied server-side (see `serverFilters` below).
  return true;
}

function matchesResourcingFilter(plan: ResourcePlanSummaryView, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "none") return !plan.resourcingStatus;
  return plan.resourcingStatus === filter;
}

/** Every distinct Constraint name this group carries across the plans already loaded —
 * same client-side derivation as the Client picker below, so an actor only ever sees
 * Constraints they can actually see a plan for. */
function constraintOptions(plans: readonly ResourcePlanSummaryView[], group: string): string[] {
  const names = new Set<string>();
  for (const plan of plans) {
    for (const tag of plan.solutionConstraints ?? []) {
      if (tag.group === group) names.add(tag.name);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

function matchesConstraint(
  plan: ResourcePlanSummaryView,
  group: string,
  selected: string,
): boolean {
  if (selected === "all") return true;
  return (plan.solutionConstraints ?? []).some(
    (tag) => tag.group === group && tag.name === selected,
  );
}

/** Single-choice chip: picking the chosen value again clears it back to "all". */
function pick<T extends string>(current: T | "all", value: T): T | "all" {
  return current === value ? "all" : value;
}

/** Label for the status filter's chip — the two groupings read as their own names. */
function statusChipLabel(filter: string): string {
  if (filter === "open") return "Current plans";
  if (filter === "inactive") return "Inactive plans";
  return `Status: ${planStatusLabel(filter)}`;
}

interface FilterChip {
  key: string;
  label: string;
  clear: () => void;
}

export function PlansListPage() {
  const router = useRouter();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [clientId, setClientId] = useState<string>("all");
  const [domain, setDomain] = useState<string>("all");
  const [icp, setIcp] = useState<string>("all");
  const [platform, setPlatform] = useState<string>("all");
  const [resourcing, setResourcing] = useState<string>("all");
  const [reviewStage, setReviewStage] = usePlanReviewFilter();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 200);
  const [creating, setCreating] = useState(false);
  const { columns, toggle: toggleColumn, reset: resetColumns } = usePlanListColumns();

  // "All plans" / "Current plans" / "Inactive plans" are client-side groupings; only a real
  // status is sent to the server, so the groups still see every status underneath them.
  const serverFilters = useMemo(
    () => (isResourcePlanStatus(statusFilter) ? { status: statusFilter } : {}),
    [statusFilter],
  );

  const { data: plans, isLoading, isError, error, refetch } = useResourcePlans(serverFilters);

  // Client options come from the plans already loaded, so the picker only ever offers
  // Clients this actor can actually see plans for. Filtered client-side: the list is
  // capped server-side and already in hand, and a server filter would shrink the picker
  // to the one Client selected.
  const clientOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const plan of plans ?? []) byId.set(plan.clientAccountId, plan.clientAccountName);
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [plans]);

  const domainOptions = useMemo(() => constraintOptions(plans ?? [], "Domain"), [plans]);
  const icpOptions = useMemo(() => constraintOptions(plans ?? [], "ICP"), [plans]);
  const platformOptions = useMemo(() => constraintOptions(plans ?? [], "Platform"), [plans]);

  // Every filter except the review-stage tile. The tiles count over this set, so each
  // tile's number is the rows you get by clicking it, and all three stay visible while
  // one is selected.
  const facetPlans = useMemo(
    () =>
      (plans ?? []).filter(
        (p) =>
          (clientId === "all" || p.clientAccountId === clientId) &&
          matchesStatusFilter(p, statusFilter) &&
          matchesConstraint(p, "Domain", domain) &&
          matchesConstraint(p, "ICP", icp) &&
          matchesConstraint(p, "Platform", platform) &&
          matchesResourcingFilter(p, resourcing) &&
          matchesPlanSearch(p, debouncedSearch),
      ),
    [plans, clientId, statusFilter, domain, icp, platform, resourcing, debouncedSearch],
  );
  const reviewCounts = useMemo(() => countPlanReviewStages(facetPlans), [facetPlans]);
  const visiblePlans = useMemo(
    () => facetPlans.filter((p) => matchesReviewStageFilter(p, reviewStage)),
    [facetPlans, reviewStage],
  );
  const clientName = clientOptions.find((c) => c.id === clientId)?.name;

  // Applied filters, shown as removable chips under the toolbar so the panel itself can
  // stay closed ("Calm first view", docs/design-system.md).
  const chips: FilterChip[] = [];
  if (reviewStage !== "all")
    chips.push({
      key: "review",
      label: `Review: ${PLAN_REVIEW_STAGE_COPY[reviewStage].label}`,
      clear: () => setReviewStage("all"),
    });
  if (statusFilter !== "all")
    chips.push({
      key: "status",
      label: statusChipLabel(statusFilter),
      clear: () => setStatusFilter("all"),
    });
  if (resourcing !== "all")
    chips.push({
      key: "resourcing",
      // "none" finds no status, which labels as "Not set".
      label: `Resourcing: ${resourcingStatusLabel(PLAN_RESOURCING_STATUSES.find((s) => s === resourcing))}`,
      clear: () => setResourcing("all"),
    });
  if (clientId !== "all")
    chips.push({
      key: "account",
      label: `Account: ${clientName ?? "Unknown"}`,
      clear: () => setClientId("all"),
    });
  if (domain !== "all")
    chips.push({ key: "domain", label: `Domain: ${domain}`, clear: () => setDomain("all") });
  if (icp !== "all") chips.push({ key: "icp", label: `ICP: ${icp}`, clear: () => setIcp("all") });
  if (platform !== "all")
    chips.push({
      key: "platform",
      label: `Platform: ${platform}`,
      clear: () => setPlatform("all"),
    });
  const clearFilters = () => {
    setReviewStage("all");
    setStatusFilter("all");
    setResourcing("all");
    setClientId("all");
    setDomain("all");
    setIcp("all");
    setPlatform("all");
  };
  const filtersActive = chips.length > 0 || debouncedSearch.trim() !== "";
  // First run: no plans and nothing filtering them — lead to New Plan, not a bare toolbar.
  const firstRun = !isLoading && !isError && plans?.length === 0 && !filtersActive;

  // The Solution picker for a new plan. Reuses the dashboard read rather than adding an
  // endpoint — it already returns every Solution this actor can see, with its Account.
  const { data: solutions, isLoading: solutionsLoading } = useSolutionPickerOptions(creating);

  if (creating) {
    return (
      <PageContainer>
        <PageHeader
          eyebrow="Manage"
          title="New Resource Plan"
          description="Set the frame once. What you choose here decides how many columns the grid has, how hours are entered, and what the margin is measured against."
        />

        <PlanSetupForm
          solutions={solutions ?? []}
          solutionsLoading={solutionsLoading}
          onCancel={() => setCreating(false)}
          onCreated={(planId) =>
            router.navigate({ to: "/dashboard/plans/$planId", params: { planId } })
          }
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer width="full">
      <PageHeader
        eyebrow="Manage"
        title="Resource Plans"
        description="Solution-level commercial and staffing planning — hours by month, priced from a governed Rate Card."
        actions={<Button onClick={() => setCreating(true)}>New Plan</Button>}
      />

      {!firstRun && !isError && (
        <PlanReviewTiles
          counts={isLoading ? null : reviewCounts}
          active={reviewStage}
          onSelect={(stage) => setReviewStage(stage === "all" ? "all" : pick(reviewStage, stage))}
        />
      )}

      {!firstRun && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={search}
              onValueChange={setSearch}
              placeholder="Search plans, Solutions, Accounts, Resources…"
              className="h-9 w-[320px]"
            />

            <FilterPopover
              trigger={() => (
                <>
                  <SlidersHorizontal className="size-4" />
                  <span>Filters{chips.length > 0 ? ` (${chips.length})` : ""}</span>
                </>
              )}
            >
              {() => (
                <div>
                  <FilterSection label="Plan status">
                    <ChipRow>
                      <ChipToggle
                        active={statusFilter === "open"}
                        onToggle={() => setStatusFilter(pick(statusFilter, "open"))}
                      >
                        Current
                      </ChipToggle>
                      <ChipToggle
                        active={statusFilter === "inactive"}
                        onToggle={() => setStatusFilter(pick(statusFilter, "inactive"))}
                      >
                        Inactive
                      </ChipToggle>
                      {SELECTABLE_PLAN_STATUSES.map((s) => (
                        <ChipToggle
                          key={s}
                          active={statusFilter === s}
                          onToggle={() => setStatusFilter(pick(statusFilter, s))}
                        >
                          {planStatusLabel(s)}
                        </ChipToggle>
                      ))}
                    </ChipRow>
                  </FilterSection>

                  <FilterSection label="Resourcing">
                    <ChipRow>
                      <ChipToggle
                        active={resourcing === "none"}
                        onToggle={() => setResourcing(pick(resourcing, "none"))}
                      >
                        Not set
                      </ChipToggle>
                      {PLAN_RESOURCING_STATUSES.map((s) => (
                        <ChipToggle
                          key={s}
                          active={resourcing === s}
                          onToggle={() => setResourcing(pick(resourcing, s))}
                        >
                          {resourcingStatusLabel(s)}
                        </ChipToggle>
                      ))}
                    </ChipRow>
                  </FilterSection>

                  {clientOptions.length > 0 && (
                    <FilterSection label="Account">
                      <SearchableMultiSelect
                        options={clientOptions}
                        selected={clientId === "all" ? [] : [clientId]}
                        placeholder="Search Accounts…"
                        onToggle={(id) => setClientId(pick(clientId, id))}
                      />
                    </FilterSection>
                  )}

                  {domainOptions.length > 0 && (
                    <FilterSection label="Domain">
                      <ChipRow>
                        {domainOptions.map((name) => (
                          <ChipToggle
                            key={name}
                            active={domain === name}
                            onToggle={() => setDomain(pick(domain, name))}
                          >
                            {name}
                          </ChipToggle>
                        ))}
                      </ChipRow>
                    </FilterSection>
                  )}

                  {icpOptions.length > 0 && (
                    <FilterSection label="ICP">
                      <ChipRow>
                        {icpOptions.map((name) => (
                          <ChipToggle
                            key={name}
                            active={icp === name}
                            onToggle={() => setIcp(pick(icp, name))}
                          >
                            {name}
                          </ChipToggle>
                        ))}
                      </ChipRow>
                    </FilterSection>
                  )}

                  {platformOptions.length > 0 && (
                    <FilterSection label="Platform">
                      <ChipRow>
                        {platformOptions.map((name) => (
                          <ChipToggle
                            key={name}
                            active={platform === name}
                            onToggle={() => setPlatform(pick(platform, name))}
                          >
                            {name}
                          </ChipToggle>
                        ))}
                      </ChipRow>
                    </FilterSection>
                  )}
                </div>
              )}
            </FilterPopover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Columns3 />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel>Show columns</DropdownMenuLabel>
                {PLAN_LIST_COLUMNS.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.key}
                    checked={columns.includes(c.key)}
                    onCheckedChange={() => toggleColumn(c.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={resetColumns}>Reset to default</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {plans && plans.length > 0 && (
              <span className="ml-auto text-sm text-muted-foreground">
                {filtersActive
                  ? `Showing ${visiblePlans.length} of ${plans.length} plans`
                  : `${plans.length} plans`}
              </span>
            )}
          </div>

          {chips.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {chips.map((c) => (
                <span
                  key={c.key}
                  className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-sm font-medium text-foreground"
                >
                  {c.label}
                  <button
                    type="button"
                    onClick={c.clear}
                    aria-label={`Remove ${c.label}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm text-muted-foreground underline-offset-2 hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Could not load Resource Plans."}
          onRetry={refetch}
        />
      )}

      {firstRun && (
        <EmptyState
          icon={FileSpreadsheet}
          title="No Resource Plans yet"
          description="A plan prices a Solution's hours by month from a governed Rate Card."
          action={
            <Button variant="outline" onClick={() => setCreating(true)}>
              New Plan
            </Button>
          }
        />
      )}

      {!isLoading && !isError && !firstRun && (
        <PlansTable
          plans={visiblePlans}
          showSolution
          showClient
          columns={columns}
          defaultSort={{ column: "start", direction: "asc" }}
          emptyMessage={`No plans match these filters${clientName ? ` for ${clientName}` : ""}.`}
        />
      )}
    </PageContainer>
  );
}
